import React, { useEffect, useMemo, useRef, useState } from "react";

const fmt = (n, d = 6) => (Number.isFinite(n) ? n.toFixed(d) : "");

export default function AtonTable({
    atons = [],                 // [{ id, name_ko, lat, lng }, ...]
    defaultSelected = [],
    selectedIds,
    onChangeSelected,           // (ids: string[]) => void
    onRowDoubleClick,           // ⬅️ 추가: (aton) => void  (예: 지도 flyTo)
    height = 320,
}) {
    const [selected, setSelected] = useState(() => new Set(defaultSelected));

    const cbRef = useRef(onChangeSelected);

    useEffect(() => {
        if (!selectedIds) return;                  // 제어 안 하면 무시
        setSelected(new Set(selectedIds.map(String)));
    }, [selectedIds]);

    useEffect(() => { cbRef.current = onChangeSelected; }, [onChangeSelected]);

    const lastSentKeyRef = useRef("");
    const headerCbRef = useRef(null);

    useEffect(() => {
        setSelected(prev => {
            const validIds = new Set(atons.map(a => String(a.id)));
            const next = new Set();
            prev.forEach(id => { if (validIds.has(id)) next.add(id); });
            return next;
        });
    }, [atons]);

    useEffect(() => {
        const ids = Array.from(selected);
        const key = ids.join("|");
        if (key !== lastSentKeyRef.current) {
            lastSentKeyRef.current = key;
            cbRef.current?.(ids);
        }
    }, [selected]);

    const allIds = useMemo(() => atons.map(a => String(a.id)), [atons]);
    const allChecked = allIds.length > 0 && allIds.every(id => selected.has(id));
    const indeterminate = selected.size > 0 && !allChecked;

    const toggleAll = () => {
        setSelected(prev => (allChecked ? new Set() : new Set(allIds)));
    };

    const toggleOne = (id) => {
        setSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    useEffect(() => {
        if (headerCbRef.current) headerCbRef.current.indeterminate = indeterminate;
    }, [indeterminate]);

    return (
        <div>
            <div style={{ fontWeight: 700, marginBottom: 8 }}>항로표지 목록</div>

            <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                    <thead style={{ background: "#f9fafb" }}>
                        <tr>
                            <th style={{ width: 44, padding: "8px 10px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>
                                <input
                                    ref={headerCbRef}
                                    type="checkbox"
                                    checked={allChecked}
                                    onChange={toggleAll}
                                />
                            </th>
                            <th style={{ padding: "8px 10px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>이름</th>
                            <th style={{ padding: "8px 16px", textAlign: "center", borderBottom: "1px solid #e5e7eb" }}>위도</th>
                            <th style={{ padding: "8px 10px", textAlign: "left", borderBottom: "1px solid #e5e7eb" }}>경도</th>
                        </tr>
                    </thead>
                </table>

                <div style={{ maxHeight: height, overflow: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
                        <tbody>
                            {atons.map((a) => {
                                const id = String(a.id);
                                const checked = selected.has(id);

                                // lat/lng 수치 보정 (문자열이면 숫자로)
                                const latNum = typeof a.lat === "string" ? parseFloat(a.lat) : a.lat;
                                const lngNum = typeof a.lng === "string" ? parseFloat(a.lng) : a.lng;

                                const handleDbl = () => {
                                    if (Number.isFinite(latNum) && Number.isFinite(lngNum)) {
                                        onRowDoubleClick?.({ ...a, lat: latNum, lng: lngNum });
                                    } else {
                                        console.warn("Invalid lat/lng on row double click:", a);
                                    }
                                };

                                return (
                                    <tr
                                        key={id}
                                        onDoubleClick={handleDbl}
                                        style={{
                                            borderBottom: "1px solid #f1f5f9",
                                            cursor: "pointer",
                                            userSelect: "none",              // 텍스트 선택 방지로 dblclick 안정화
                                            WebkitUserSelect: "none",
                                        }}
                                        // 일부 환경에서 dblclick이 잡히지 않을 때 대비(수동 감지)
                                        onClick={(e) => {
                                            const now = performance.now();
                                            if (!e.currentTarget._lastClickAt) {
                                                e.currentTarget._lastClickAt = now;
                                            } else {
                                                if (now - e.currentTarget._lastClickAt < 250) {
                                                    handleDbl();                 // 250ms 내 2회 클릭 → 수동 더블클릭
                                                }
                                                e.currentTarget._lastClickAt = now;
                                            }
                                        }}
                                        title="더블클릭하면 지도에서 위치로 이동"
                                    >
                                        <td style={{ width: 44, padding: "8px 10px", textAlign: "center" }}>
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleOne(id)}
                                                onClick={(e) => e.stopPropagation()}     // 행 더블클릭과 충돌 방지
                                                onDoubleClick={(e) => e.stopPropagation()}
                                            />
                                        </td>
                                        <td style={{ padding: "8px 10px" }}>
                                            {a.name_ko || a.name_en || "표지"}
                                        </td>
                                        <td style={{ padding: "8px 10px", textAlign: "left", fontVariantNumeric: "tabular-nums" }}>
                                            {fmt(latNum)}
                                        </td>
                                        <td style={{ padding: "8px 10px", textAlign: "left", fontVariantNumeric: "tabular-nums" }}>
                                            {fmt(lngNum)}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div style={{ marginTop: 8, fontSize: 12, color: "#6b7280" }}>
                선택: {selected.size} / {atons.length}
            </div>
        </div>
    );
}
